/**
 * Header del lobby: avatar con nivel, contador de monedas y de gemas.
 * Siempre visible, independiente de la pestaña activa.
 */

import Phaser from 'phaser';
import { TEX } from '../../core/TextureFactory';
import type { MetaState } from '../../core/types';
import { AudioToggleButton } from '../AudioToggleButton';
import { PALETTE } from './palette';
import { formatCompact } from './uiKit';

export class LobbyHeader extends Phaser.GameObjects.Container {
  private readonly levelText: Phaser.GameObjects.Text;
  private readonly coinsText: Phaser.GameObjects.Text;
  private readonly gemsText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, width: number, meta: MetaState) {
    super(scene, 0, 0);

    const barHeight = 68;
    const bar = scene.add.graphics();
    bar.fillStyle(PALETTE.panel, 1);
    bar.fillRect(0, 0, width, barHeight);
    bar.lineStyle(2, PALETTE.border, 1);
    bar.lineBetween(0, barHeight, width, barHeight);

    // avatar circular con el nivel adentro
    const avatarX = 40;
    const avatarY = barHeight / 2;
    const avatar = scene.add.graphics();
    avatar.fillStyle(PALETTE.panel2, 1);
    avatar.fillCircle(avatarX, avatarY, 24);
    avatar.lineStyle(3, PALETTE.accent, 1);
    avatar.strokeCircle(avatarX, avatarY, 24);
    avatar.fillStyle(0x3f96d6, 1);
    avatar.fillCircle(avatarX, avatarY - 4, 9); // cabeza simple
    avatar.fillTriangle(avatarX - 12, avatarY + 16, avatarX + 12, avatarY + 16, avatarX, avatarY + 2);

    const levelBadge = scene.add.graphics();
    levelBadge.fillStyle(PALETTE.accent, 1);
    levelBadge.fillRoundedRect(avatarX - 14, avatarY + 14, 28, 16, 6);
    this.levelText = scene.add
      .text(avatarX, avatarY + 22, `${meta.level}`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#241c3d',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // contador de monedas
    const coinsX = width - 150;
    const coinIcon = scene.add.image(coinsX, avatarY, TEX.iconCoin).setDisplaySize(20, 20);
    this.coinsText = scene.add
      .text(coinsX + 16, avatarY, formatCompact(meta.coins), {
        fontFamily: 'monospace',
        fontSize: '15px',
        color: '#f4f0ff',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    // contador de gemas
    const gemsX = width - 66;
    const gemIcon = scene.add.image(gemsX, avatarY, TEX.iconGem).setDisplaySize(20, 20);
    this.gemsText = scene.add
      .text(gemsX + 16, avatarY, formatCompact(meta.gems), {
        fontFamily: 'monospace',
        fontSize: '15px',
        color: '#f4f0ff',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    // silenciar/activar sonido (persiste entre escenas vía AudioManager).
    // Va en el hueco entre el avatar y los contadores para no pisar ninguno.
    const muteButton = new AudioToggleButton(scene, avatarX + 64, avatarY);

    this.add([
      bar,
      avatar,
      levelBadge,
      this.levelText,
      coinIcon,
      this.coinsText,
      gemIcon,
      this.gemsText,
      muteButton,
    ]);
    this.setSize(width, barHeight);
    scene.add.existing(this);
  }

  /** Actualiza los contadores mostrados (llamar tras cualquier cambio de meta). */
  refresh(meta: MetaState): void {
    this.levelText.setText(`${meta.level}`);
    this.coinsText.setText(formatCompact(meta.coins));
    this.gemsText.setText(formatCompact(meta.gems));
  }
}

/** Alto fijo del header, para que el resto del layout calcule su espacio disponible. */
export const HEADER_HEIGHT = 68;
